import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getSource, setSource } from "../../services/api/sourceService";

// Which accounting system the portal is showing (Bill.com or IES). Global on
// the API, so this slice reads it on load and writes it back on change; every
// list that shows provider-owned records asks for the active source.

export type SourceState = {
  source: string;
  options: string[];
  labels: Record<string, string>;
  loading: boolean;
  saving: boolean;
  error: string | null;
  // Whether a read of the source has finished, successfully or not. A list
  // needs it to know whether the empty source it is holding is "the switch has
  // not answered yet" or "nobody is going to ask" — the two look identical in
  // `source` alone, and treating the first as the second is how a page ends up
  // asking for its list twice.
  settled: boolean;
  // True when the API refused to tell this session the source. It can then stop
  // being asked for: the switch is only for those allowed to read it, and a
  // refusal is a final answer rather than something to retry on every page.
  forbidden: boolean;
};

const initialState: SourceState = {
  // Empty until the API answers. A list omits the parameter when it is empty,
  // so the server's active source decides instead of a browser-side default.
  source: "",
  options: ["bill", "ies"],
  labels: { bill: "Bill.com", ies: "IES" },
  loading: false,
  saving: false,
  error: null,
  settled: false,
  forbidden: false,
};

// canSwitchSource reports whether this user gets the switch. It decides whether
// the source is read at all, so a list that waits for it has to ask the same
// question: a user who cannot switch will never be answered, and waiting would
// leave their page empty.
export function canSwitchSource(user: any): boolean {
  return !!user && (user.role === "super-admin" || !!user.permissions?.integrations);
}

export const fetchSource = createAsyncThunk("source/fetch", async (_: void, { rejectWithValue }) => {
  try {
    return await getSource();
  } catch (error: any) {
    return rejectWithValue({
      message: error.message || "Failed to read the data source",
      status: error.status,
    });
  }
});

export const saveSource = createAsyncThunk("source/save", async (source: string, { rejectWithValue }) => {
  try {
    return await setSource(source);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to switch the data source");
  }
});

const sourceSlice = createSlice({
  name: "source",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSource.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSource.fulfilled, (state, action: any) => {
        state.loading = false;
        state.settled = true;
        state.source = action.payload?.source ?? state.source;
        if (Array.isArray(action.payload?.options) && action.payload.options.length) {
          state.options = action.payload.options;
        }
        if (action.payload?.labels) {
          state.labels = action.payload.labels;
        }
      })
      .addCase(fetchSource.rejected, (state, action: any) => {
        state.loading = false;
        // Settled even on failure: a list must not wait for a read that has
        // already given up, it must fall back to the server's own answer.
        state.settled = true;
        state.error = action.payload?.message ?? "Failed to read the data source";
        // A refusal means this session is not meant to see the source at all.
        state.forbidden = action.payload?.status === 403;
      })
      .addCase(saveSource.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveSource.fulfilled, (state, action: any) => {
        state.saving = false;
        state.source = action.payload?.source ?? state.source;
      })
      .addCase(saveSource.rejected, (state, action: any) => {
        state.saving = false;
        state.error = action.payload ?? "Failed to switch the data source";
      });
  },
});

export default sourceSlice.reducer;
